import { gql, useMutation } from '@apollo/client'

import { CreatePaymentMethodResponse } from '@/clients/utoniq-core/schema'
import { useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import {
  getRedirectAfterCreditCardRegisterPath,
  removeRedirectAfterCreditCardRegisterPath,
} from '@/core/native/storage'
import { sleep } from '@/lib/sleep'
import { confirmCardSetupWithStripe, createTokenWithCard } from '@/lib/stripe'

import { Action, fragments, State } from './template'

type CreatePaymentMethodRes = {
  createPaymentMethod: CreatePaymentMethodResponse
}

type CreatePaymentMethodVars = {
  createPaymentMethodToken: string
}

export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation()
  const { router, routerPages, location } = useRouter<
    unknown,
    { goBackPath?: string }
  >()

  const [createPaymentMethod] = useMutation<
    CreatePaymentMethodRes,
    CreatePaymentMethodVars
  >(PAYMENT_METHOD_CREATE)

  return {
    state: {},
    actions: {
      /**
       * @description
       * 一連の処理の流れは以下の通り
       * 1. カード登録に必要なトークンの発行
       * 2. 登録リクエストを投げると、正常終了 or 3DSecure 認証要求
       * 3. （3DSecure 認証が求められた場合のみ）
       *    3DSecure モーダルで認証を行う
       * 4. 適切な画面にリダイレクト
       */
      onSubmitCreatePayment: async values => {
        const [expMonth, expYear] = values.expirationDate.split('/')
        /**
         * @description
         * クレジットカード登録に必要となるトークンを
         * 決済サービスプロバイダから発行する
         */
        const token = await createTokenWithCard({
          number: values.cardNumber.replace(/\s/g, ''),
          name: values.cardHolder,
          cvc: values.securityCode.replace(/\s/g, ''),
          expMonth: expMonth,
          expYear: expYear,
        }).catch(error => {
          throw new Error(t(error.message))
        })
        /**
         * @description
         * トークンを元にクレジットカード登録を
         * 決済プロバイダーにリクエストする
         * 3DSecure が求められなかった場合 CreditCard 型を、
         * 3DSecure が求められた場合 RequiresAction 型を
         * value に持つデータが取得される
         */
        const { data: paymentResponse } = await createPaymentMethod({
          variables: { createPaymentMethodToken: token },
        })
        /**
         * @description
         * 3DSecure が求められた場合
         * すなわち RequiresAction 型のデータが取得された場合の処理
         */
        if (
          paymentResponse &&
          'clientSecret' in paymentResponse.createPaymentMethod
        ) {
          const { clientSecret } = paymentResponse.createPaymentMethod
          /**
           * @description
           * ここでモーダルが起動される
           */
          const result = await confirmCardSetupWithStripe(clientSecret)
          if (result.error) throw new Error(result.error.message)
        }
        /**
         * @description
         * webhook通知〜クレジットカード登録の完了を待つため
         * 暫定的に sleep を加えている
         */
        await sleep(2000)
        /**
         * @description
         * 登録後のリダイレクト処理
         */
        const { value: redirectPath } =
          await getRedirectAfterCreditCardRegisterPath()
        if (redirectPath) {
          router.push(redirectPath)
          removeRedirectAfterCreditCardRegisterPath()
        } else {
          router.goBack(location.state?.goBackPath || routerPages.PaymentDetail)
        }
      },
    },
  }
})

const PAYMENT_METHOD_CREATE = gql`
  mutation createPaymentMethod($createPaymentMethodToken: String!) {
    createPaymentMethod(token: $createPaymentMethodToken) {
      ...paymentCreateDetail
      ...requiresActionDetail
    }
  }
  ${fragments.credit}
  ${fragments.requiresAction}
`
